import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

function checks(pw) {
  return {
    len: pw.length >= 8,
    letter: /[A-Za-z]/.test(pw),
    digit: /\d/.test(pw),
  };
}

export default function ChangePassword() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const c = useMemo(() => checks(pw), [pw]);
  const allValid = current && c.len && c.letter && c.digit && pw === confirm && pw !== current;

  const submit = async (e) => {
    e.preventDefault();
    if (!allValid) { toast.error("Please meet all password rules"); return; }
    setLoading(true);
    try {
      const r = await api.post("/auth/change-password", {
        current_password: current, new_password: pw,
      });
      if (r.data?.token) {
        localStorage.setItem("token", r.data.token);
      }
      toast.success("Password changed. Every other session has been signed out.");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="w-full max-w-md border-slate-200 shadow-sm">
        <CardContent className="p-8" data-testid="change-password-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold">Set a new password</h2>
              <p className="text-sm text-slate-500 mt-1">
                Signed in as <b>{user?.email}</b>. Your other sessions will be signed out.
              </p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="cur">Current or temporary password</Label>
              <Input id="cur" type="password" required autoFocus
                     value={current} onChange={(e) => setCurrent(e.target.value)}
                     data-testid="change-password-current" />
            </div>
            <div>
              <Label htmlFor="new">New password</Label>
              <Input id="new" type="password" required
                     value={pw} onChange={(e) => setPw(e.target.value)}
                     data-testid="change-password-new" />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm</Label>
              <Input id="confirm" type="password" required
                     value={confirm} onChange={(e) => setConfirm(e.target.value)}
                     data-testid="change-password-confirm" />
            </div>
            <ul className="text-xs space-y-1">
              <li className={c.len ? "text-emerald-700" : "text-slate-500"}>• At least 8 characters</li>
              <li className={c.letter ? "text-emerald-700" : "text-slate-500"}>• At least one letter</li>
              <li className={c.digit ? "text-emerald-700" : "text-slate-500"}>• At least one number</li>
              <li className={pw && pw === confirm ? "text-emerald-700" : "text-slate-500"}>• Both entries match</li>
              <li className={pw && current && pw !== current ? "text-emerald-700" : "text-slate-500"}>• Different from your current password</li>
            </ul>
            <div className="flex items-center gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { logout(); navigate("/login"); }}
                      data-testid="change-password-signout">
                Sign out
              </Button>
              <Button type="submit" disabled={loading || !allValid}
                      className="flex-1 bg-[#0F172A] hover:bg-slate-800"
                      data-testid="change-password-submit">
                {loading ? "Updating..." : "Set new password"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
